import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import styles from '../Auth/Auth.module.css'

type MessageType = 'success' | 'error' | ''

interface Message {
    type: MessageType
    text: string
}

interface ChangePasswordProps {
    onComplete: () => void
}

export default function ChangePassword({ onComplete }: Readonly<ChangePasswordProps>) {
    const { t } = useTranslation()
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<Message>({ type: '', text: '' })

    const showMessage = (type: Exclude<MessageType, ''>, text: string) => {
        setMessage({ type, text })
    }

const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()
        
        if (password !== confirmPassword) {
            showMessage('error', t('changePassword.errors.mismatch'))
            return
        }

        setLoading(true)
        setMessage({ type: '', text: '' })

        try {
            const { error } = await supabase.auth.updateUser({ password })
            if (error) throw error

            showMessage('success', t('changePassword.errors.success'))
            setTimeout(() => {
                onComplete()
            }, 2000)
        } catch (error) {
            showMessage('error', (error as Error).message)
            setLoading(false)
        }
    }

    return (
        <div className={styles.authContainer}>
            <div className={styles.authBackground}>
                <div className={`${styles.authBlob} ${styles.authBlob1}`}></div>
                <div className={`${styles.authBlob} ${styles.authBlob2}`}></div>
                <div className={`${styles.authBlob} ${styles.authBlob3}`}></div>
            </div>

            <div className={`${styles.authCard} animate-fade-in-up`}>
                <div className={`${styles.authLogo} animate-float`}>
                    <span className={styles.authLogoIcon}>🔒</span>
                </div>

                <h1 className={styles.authTitle}>{t('changePassword.title')}</h1>
                <p className={styles.authSubtitle}>{t('changePassword.subtitle')}</p>

                <form onSubmit={handleSubmit} className={styles.authForm}>
                    <div className={styles.inputGroup}>
                        <input
                            id="password"
                            type="password"
                            placeholder=" "
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                        />
                        <label htmlFor="password">{t('changePassword.password')}</label>
                        <span className={styles.inputIcon}>🔒</span>
                    </div>

                    <div className={styles.inputGroup}>
                        <input
                            id="confirmPassword"
                            type="password"
                            placeholder=" "
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                        />
                        <label htmlFor="confirmPassword">{t('changePassword.confirm')}</label>
                        <span className={styles.inputIcon}>🔒</span>
                    </div>

                    {message.text && (
                        <div className={`${styles.authMessage} ${styles[message.type]} animate-fade-in`}>
                            <span className={styles.messageIcon}>{message.type === 'success' ? '✓' : '⚠'}</span>
                            {message.text}
                        </div>
                    )}

                    <button type="submit" className={`${styles.authSubmit} btn-primary`} disabled={loading}>
                        {loading ? (
                            <>
                                <span className={styles.spinner}></span>{' '}
                                {t('changePassword.loading')}
                            </>
                        ) : (
                            t('changePassword.submit')
                        )}
                    </button>
                </form>
            </div>
        </div>
    )
}
